// src/components/common/InfoTooltip.tsx

import React from 'react';
import { Tooltip } from '@mui/material';
import { InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';

interface InfoTooltipProps {
  // The explanatory text shown in the popover.
  text: string;
  // Defaults match the help icons in AddPaymentModal and UserDialog.
  fontSize?: string;
  color?: string;
}

// Shared "(i)" icon with a hover/focus/tap popover explaining a field.
//
// Two behaviours are load-bearing, not stylistic preferences:
// - enterTouchDelay={0}: MUI's default (700ms) requires a deliberate long
//   press on touch devices before the tooltip appears, which looks
//   identical to "this is broken" on a normal tap. A help affordance
//   should reveal on a normal tap.
// - slotProps.popper.zIndex: MUI's default Popper stacking sits below
//   this app's modal/dialog backdrop, so without this override the
//   tooltip renders visually behind the dialog it belongs to. Same root
//   cause already fixed for CustomSelect and DateRangePicker's dropdowns.
const InfoTooltip: React.FC<InfoTooltipProps> = ({
  text,
  fontSize = '2.5rem',
  color = 'secondary.main',
}) => (
  <Tooltip
    title={text}
    arrow
    placement="top"
    enterTouchDelay={0}
    leaveTouchDelay={5000}
    slotProps={{
      popper: { sx: { zIndex: 2200 } },
      tooltip: {
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          fontSize: '1.4rem',
        },
      },
      arrow: {
        sx: {
          color: 'rgba(0, 0, 0, 0.9)',
        },
      },
    }}
  >
    <InfoOutlinedIcon
      tabIndex={0}
      sx={{
        fontSize,
        color,
        cursor: 'help',
        flexShrink: 0,
      }}
    />
  </Tooltip>
);

export default InfoTooltip;
